/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

/**
 * offlineQueue.js — Gestione della coda operazioni offline (Fase 2 + 3)
 *
 * Funzioni esportate:
 *   enqueue(opType, req)   → aggiunge un'operazione e registra Background Sync
 *   countPending()         → numero operazioni in attesa
 *   syncPending(token)     → invia tutte le operazioni in coda al server
 */
import axios from 'axios';
import { db } from './offlineDb';

/**
 * Accoda un'operazione di scrittura e, se disponibile, registra un tag
 * Background Sync perché il browser riprovi automaticamente.
 *
 * @param {string} opType  Label leggibile ('createSoccorritore' | 'markPartenza' | 'updateMissioneStato')
 * @param {{url: string, method: string, body?: object, params?: object, clientId?: string}} req
 *   clientId: id client stabile per le creazioni offline (es. `offline_…`). Permette
 *   di ritrovare la POST in coda e fondervi le modifiche successive (coalesceUpdate)
 *   o rimuoverla in caso di eliminazione (dropPending) prima della sincronizzazione.
 */
/** Operatore "in turno" indicato localmente (cambio turno offline su PCA
 *  condiviso). Catturato al momento dell'accodamento così l'azione viene
 *  attribuita a chi operava ALLORA, non a chi è in turno al successivo sync. */
function actingOperatorName() {
  try { return localStorage.getItem('tas_acting_operator') || null; }
  catch { return null; }
}

export async function enqueue(opType, req) {
  await db.pendingOps.add({
    opType,
    url:       req.url,
    method:    req.method,
    body:      req.body   ?? null,
    params:    req.params ?? null,
    clientId:  req.clientId ?? null,
    // Chiave di idempotenza della creazione: rispedita identica a ogni tentativo,
    // così un replay dopo una caduta di rete non genera un doppione lato server.
    idempotencyKey: req.idempotencyKey ?? null,
    acting:    req.acting ?? actingOperatorName(),
    // Upload file (multipart): IndexedDB conserva il File via structured clone;
    // al sync viene ricostruita la FormData. Usato per l'import zone GM offline.
    file:      req.file      ?? null,
    fileField: req.fileField ?? null,
    timestamp: Date.now(),
    retries:   0,
  });

  // Notifica MainApp per aggiornare il badge sidebar
  window.dispatchEvent(new CustomEvent('offline-op-queued'));

  // Fase 3 — Background Sync API (Chrome/Android, non disponibile su iOS)
  // Il SW riceverà l'evento 'sync' e invierà SYNC_NEEDED al main thread
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      if ('sync' in registration) {
        await registration.sync.register('tas-offline-sync');
      }
    } catch {
      // Background Sync non supportato — il fallback visibilitychange/focus
      // in App.js garantisce comunque la sincronizzazione su iOS/Safari
    }
  }
}

/** Endpoint che NON vanno mai rigiocati automaticamente: autenticazione e affini.
 *  Rimandarli dopo un errore di rete non ha senso (login/reset sono interattivi) e
 *  rispedirli a distanza di tempo sarebbe anche sgradevole dal lato sicurezza. */
const NO_REPLAY_RE = /\/(auth|profilo\/cambia-password)\b/i;

/**
 * Accoda una scrittura RICOSTRUENDOLA dalla config axios di una richiesta fallita
 * per errore di RETE (nessuna risposta dal server).
 *
 * PERCHÉ ESISTE: l'accodamento scattava solo se l'app SAPEVA già di essere offline.
 * Ma la connettività si verifica con un probe ogni 20s: nella finestra fra "il campo
 * cade" e "il probe se ne accorge" la richiesta parte, muore, e il `catch` del
 * chiamante mostrava un errore — l'azione era persa, senza finire in coda. In campo
 * quella finestra e' esattamente il momento in cui si opera al limite della
 * copertura. Ora la richiesta morta viene accodata come se fossimo stati offline
 * dall'inizio.
 *
 * ⚠️ Un errore di rete non dice se il server abbia gia' elaborato la richiesta
 * (la risposta puo' essersi persa al ritorno). Per POST di creazione il replay puo'
 * quindi generare un doppione: e' la finestra che chiuderebbe l'idempotency key.
 * Su PUT/PATCH/DELETE il replay e' invece innocuo.
 *
 * @returns {Promise<boolean>} true se l'operazione e' stata accodata
 */
export async function enqueueFromRequest(config) {
  if (!config) return false;
  const method = String(config.method || '').toUpperCase();
  if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) return false;   // le GET non si accodano
  const url = config.url || '';
  if (!url || NO_REPLAY_RE.test(url)) return false;

  // axios serializza il body JSON in stringa prima di inviarlo: va riportato a
  // oggetto, altrimenti al replay verrebbe rispedito come stringa.
  let body = config.data ?? null;
  if (typeof body === 'string') {
    try { body = JSON.parse(body); } catch { /* non JSON: lascialo com'e' */ }
  }
  // FormData (upload file): non ricostruibile in modo affidabile dalla config.
  // Meglio non accodare che accodare qualcosa di rotto — l'utente rivedra' l'errore.
  if (typeof FormData !== 'undefined' && body instanceof FormData) return false;

  // Riusa la chiave di idempotenza GIÀ inviata col tentativo fallito. È il pezzo che
  // rende innocuo il replay: senza, la POST ripartirebbe con una chiave nuova e il
  // server la tratterebbe come una creazione diversa — cioè un doppione, proprio nel
  // caso in cui non sappiamo se la prima fosse andata a segno.
  let idem = null;
  try {
    const h = config.headers;
    if (h) {
      idem = (typeof h.get === 'function' ? h.get('Idempotency-Key') : null)
        ?? h['Idempotency-Key'] ?? h['idempotency-key'] ?? null;
    }
  } catch { /* header non leggibili: si prosegue senza chiave */ }

  await enqueue(`replay:${method}`, {
    url, method, body,
    params: config.params ?? null,
    idempotencyKey: idem || null,
  });
  return true;
}

/**
 * Restituisce il numero di operazioni in attesa di sincronizzazione.
 * @returns {Promise<number>}
 */
export async function countPending() {
  return db.pendingOps.count();
}

/**
 * Elenco delle operazioni ancora in attesa, in ordine cronologico (lo stesso in cui
 * verranno inviate). Serve a mostrare COSA non è ancora partito: in campo, prima di
 * spegnere il telefono o di lasciare la copertura, un numero da solo non dice nulla.
 * @returns {Promise<object[]>}
 */
export async function listPending() {
  return db.pendingOps.orderBy('timestamp').toArray();
}

/**
 * Id oggetto delle DELETE offline ancora in coda (ultimo segmento dell'URL).
 * Serve a `mergeKeepOffline` per non far riapparire un record eliminato offline
 * quando si rinfrescano i dati dal server prima della sincronizzazione.
 * @returns {Promise<Set<string>>}
 */
export async function pendingDeletedIds() {
  const ops = await db.pendingOps.toArray();
  const ids = new Set();
  for (const o of ops) {
    if (o.method === 'DELETE') {
      const m = (o.url || '').match(/\/([^/]+)\/?$/);
      if (m) ids.add(m[1]);
    }
  }
  return ids;
}

/**
 * Fonde i record provenienti dal server con lo stato locale, PRESERVANDO i
 * record con modifiche offline non ancora sincronizzate (`_offline: true`).
 *
 * Serve a evitare che un refresh dei dati (anche servito dalla cache del Service
 * Worker, quindi stale) cancelli gli aggiornamenti ottimistici mostrati a video
 * mentre si lavora offline. Da usare al posto di `setState(incoming)` finché ci
 * sono operazioni in coda; quando la coda è vuota si torna a usare i dati server
 * "puliti" (i flag `_offline` spariscono perché tutto è stato sincronizzato).
 *
 * @template T
 * @param {T[]} incoming  record dal server
 * @param {T[]} current   stato locale corrente
 * @param {Set<string>} [deletedIds]  id con DELETE offline in coda: vanno esclusi
 *   dai dati server (altrimenti un record eliminato offline riapparirebbe).
 * @returns {T[]}
 */
export function mergeKeepOffline(incoming, current, deletedIds) {
  const del = deletedIds instanceof Set ? deletedIds : new Set();
  const inc = (Array.isArray(incoming) ? incoming : []).filter(r => !del.has(r.id));
  const localOffline = (current || []).filter(r => r && r._offline);
  if (localOffline.length === 0) return inc;
  const byId = new Map(localOffline.map(r => [r.id, r]));
  const serverIds = new Set(inc.map(r => r.id));
  // Per i record presenti su entrambi: tieni la versione locale se ha modifiche
  // offline pendenti, altrimenti quella del server.
  const merged = inc.map(srv => (byId.has(srv.id) ? byId.get(srv.id) : srv));
  // Aggiungi i record creati offline che il server non conosce ancora.
  const offlineOnly = localOffline.filter(r => !serverIds.has(r.id));
  return [...merged, ...offlineOnly];
}

/**
 * Fonde una modifica (PATCH) nel corpo della CREAZIONE (POST) ancora in coda con
 * lo stesso clientId. Così, per un elemento creato offline e non ancora
 * sincronizzato, le modifiche successive non vanno perse: vengono applicate alla
 * create, che al sync invierà già i dati aggiornati (niente PATCH verso un id
 * inesistente lato server).
 *
 * @param {string} clientId  id client della creazione offline (es. `offline_…`)
 * @param {object} patchBody campi modificati da fondere
 * @returns {Promise<boolean>} true se la create è stata trovata e aggiornata
 */
export async function coalesceUpdate(clientId, patchBody) {
  if (!clientId) return false;
  const ops = await db.pendingOps.toArray();
  const createOp = ops.find(o => o.clientId === clientId && o.method === 'POST');
  if (!createOp) return false;
  await db.pendingOps.update(createOp.id, { body: { ...(createOp.body || {}), ...patchBody } });
  return true;
}

/**
 * Rimuove dalla coda tutte le operazioni legate a un clientId (la creazione e
 * eventuali modifiche fuse). Usato quando un elemento creato offline viene
 * eliminato prima della sincronizzazione: non deve essere creato affatto sul
 * server.
 *
 * @param {string} clientId  id client della creazione offline (es. `offline_…`)
 * @returns {Promise<number>} numero di operazioni rimosse
 */
export async function dropPending(clientId) {
  if (!clientId) return 0;
  const ops = await db.pendingOps.toArray();
  // Rimuove la create E ogni op che fa riferimento al clientId (cambi di stato,
  // assegnazioni, ecc. che puntano all'elemento via offline_ id nell'URL/body):
  // se l'elemento non verrà creato, nemmeno le sue azioni dipendenti hanno senso.
  const ids = ops.filter(o =>
    o.clientId === clientId ||
    JSON.stringify([o.url, o.body, o.params]).includes(clientId)
  ).map(o => o.id);
  if (ids.length) {
    await db.pendingOps.bulkDelete(ids);
    window.dispatchEvent(new CustomEvent('offline-op-queued')); // aggiorna badge sidebar
  }
  return ids.length;
}

/**
 * Tetto ai tentativi sugli errori transitori (5xx / rete). Senza, `retries` cresceva
 * all'infinito: un'operazione che il backend rifiuta per un proprio bug veniva
 * ritentata a ogni sync per sempre, invisibile. Raggiunto il tetto l'op smette di
 * essere transitoria e passa fra le rifiutate, dove l'operatore la vede.
 */
const MAX_RETRIES = 8;

/**
 * Sposta un'operazione da `pendingOps` a `failedOps`, conservandone il contenuto.
 * MAI cancellare direttamente: il payload e' lavoro inserito da una persona in campo,
 * e senza di esso il messaggio "riprova" sarebbe una bugia (non c'e' piu' nulla da
 * riprovare). Qui resta finche' non viene ripresa o scartata esplicitamente.
 *
 * @param {object} op      record di pendingOps
 * @param {{status?: number, error?: string, motivo: string}} esito
 */
async function moveToFailed(op, esito) {
  const { id, ...payload } = op;
  await db.failedOps.add({
    ...payload,
    failedAt:   Date.now(),
    httpStatus: esito.status ?? null,
    error:      esito.error ?? null,
    motivo:     esito.motivo,
  });
  await db.pendingOps.delete(id);
}

/** Numero di operazioni rifiutate in attesa di revisione. */
export async function countFailed() {
  return db.failedOps.count();
}

/** Elenco delle operazioni rifiutate, dalla piu' recente. */
export async function listFailed() {
  const rows = await db.failedOps.orderBy('failedAt').toArray();
  return rows.reverse();
}

/**
 * Rimette in coda un'operazione rifiutata, per un nuovo tentativo esplicito.
 * Utile quando la causa era ambientale e non nel dato: token di un operatore con
 * permessi diversi (403 sul PCA condiviso), backend temporaneamente incoerente,
 * dipendenza risolta nel frattempo.
 */
export async function retryFailed(failedId) {
  const row = await db.failedOps.get(failedId);
  if (!row) return false;
  const { id, failedAt, httpStatus, error, motivo, ...op } = row;
  await db.pendingOps.add({ ...op, retries: 0 });
  await db.failedOps.delete(failedId);
  window.dispatchEvent(new CustomEvent('offline-op-queued'));
  return true;
}

/** Scarta definitivamente un'operazione rifiutata (scelta esplicita dell'operatore). */
export async function discardFailed(failedId) {
  await db.failedOps.delete(failedId);
  window.dispatchEvent(new CustomEvent('offline-op-queued'));
}

/** Svuota l'elenco delle rifiutate. */
export async function discardAllFailed() {
  await db.failedOps.clear();
  window.dispatchEvent(new CustomEvent('offline-op-queued'));
}

const PLACEHOLDER_RE = /offline_[a-z0-9_]+/gi;

/** Sostituisce ricorsivamente in stringhe/oggetti gli id placeholder già risolti. */
function remapValue(val, idMap) {
  if (typeof val === 'string') {
    return val.replace(PLACEHOLDER_RE, (m) => (m in idMap ? idMap[m] : m));
  }
  if (Array.isArray(val)) return val.map(v => remapValue(v, idMap));
  if (val && typeof val === 'object') {
    const out = {};
    for (const k of Object.keys(val)) out[k] = remapValue(val[k], idMap);
    return out;
  }
  return val;
}

/** True se l'op fa riferimento a un placeholder offline_ NON ancora risolto
 *  (escluso il proprio clientId, che è quello che la create sta per creare). */
function hasUnresolvedPlaceholder(op, idMap) {
  const blob = JSON.stringify([op.url, op.body, op.params]);
  const matches = blob.match(PLACEHOLDER_RE) || [];
  return matches.some(m => m !== op.clientId && !(m in idMap));
}

/**
 * Invia al server, in ordine cronologico, tutte le operazioni in coda.
 *
 * Rimappatura id (offline → reale): le creazioni offline usano un clientId
 * `offline_…` che funge da id provvisorio anche per le azioni successive
 * (cambio stato, assegnazioni, riferimenti tra entità). Quando la create
 * sincronizza, il server restituisce l'id reale: viene memorizzato e tutte le
 * op seguenti che citano quel placeholder (in URL, body o params) vengono
 * riscritte con l'id reale prima dell'invio. Un'op che dipende da una create non
 * ancora sincronizzata (es. fallita con 5xx) viene rimandata al ciclo successivo.
 *
 * Categorizzazione errori:
 *   - 401 / 429     → transitorio (token scaduto / rate limit): mantiene in coda
 *                     e INTERROMPE il ciclo (le op successive fallirebbero uguale)
 *   - 409 Conflict → conflitto dati: SPOSTA in `failedOps` (conta anche `conflicts`)
 *   - altri 4xx     → rifiuto permanente: SPOSTA in `failedOps`
 *   - 5xx / rete    → transitorio: mantiene in coda, e dopo MAX_RETRIES tentativi
 *                     SPOSTA in `failedOps`
 *
 * Nessun ramo cancella piu' un'operazione: quello che il server rifiuta finisce in
 * `failedOps` con payload ed errore, e aspetta una decisione dell'operatore.
 *
 * @param {string} token  JWT dell'operatore corrente
 * @returns {Promise<{ok: number, fail: number, conflicts: number, rejected: number, total: number}>}
 *   `fail` = ancora in coda (riproveranno da sole) · `rejected` = spostate fra le rifiutate
 */
export async function syncPending(token) {
  const ops = await db.pendingOps.orderBy('timestamp').toArray();
  if (ops.length === 0) return { ok: 0, fail: 0, conflicts: 0, total: 0 };

  const headers = { Authorization: `Bearer ${token}` };
  const idMap = {};            // offline_<clientId> → id reale restituito dal server
  const removedOpIds = new Set(); // op già eliminate come orfane in questo ciclo
  let ok = 0;
  let fail = 0;        // ancora in coda: riproveranno da sole al prossimo sync
  let conflicts = 0;
  let rejected = 0;    // spostate in failedOps: servono una decisione dell'operatore

  // Una create fallita in modo permanente non verrà mai sincronizzata → le op
  // dipendenti (che la citano via clientId) sono orfane: rimuovile subito dalla
  // coda invece di lasciarle inerti a ogni ciclo.
  const purgeDependents = async (clientId, causa) => {
    const all = await db.pendingOps.toArray();
    const toRemove = all.filter(o =>
      o.clientId !== clientId &&
      JSON.stringify([o.url, o.body, o.params]).includes(clientId)
    );
    for (const o of toRemove) {
      // Anche le dipendenti sono lavoro dell'operatore: non si buttano, si spostano
      // fra le rifiutate spiegando che a cadere e' stata la creazione da cui dipendono.
      await moveToFailed(o, { motivo: `Dipendeva da un'operazione rifiutata (${causa})` });
      removedOpIds.add(o.id);
    }
    return toRemove.length;
  };

  for (const op of ops) {
    if (removedOpIds.has(op.id)) {
      // Era una create già spostata fra le rifiutate → sposta anche ciò che dipendeva
      // da essa (cascata multi-livello, es. missione che cita una squadra orfana).
      if (op.clientId) await purgeDependents(op.clientId, 'dipendenza a cascata');
      continue;
    }
    // Dipende da una create non ancora andata a buon fine → rimanda (non inviare
    // una richiesta verso un id che il server non conosce ancora).
    if (hasUnresolvedPlaceholder(op, idMap)) continue;

    try {
      // Op con file (es. import zone GM): ricostruisci la FormData; axios imposta
      // da sé il Content-Type multipart col boundary.
      let data;
      if (op.file) {
        data = new FormData();
        data.append(op.fileField || 'file', op.file, op.file.name);
      } else {
        data = remapValue(op.body, idMap);
      }
      // Attribuzione cambio turno: rispedisce l'operatore in turno catturato
      // all'accodamento (header sicuro: URL-encoded per gli accenti).
      // X-Client-Action-Ts: istante in cui l'azione e' stata realmente compiuta
      // (timestamp catturato all'accodamento, non ora). Senza, il backend daterebbe
      // tutto col momento del sync — una battuta chiusa alle 14:05 e sincronizzata
      // alle 16:30 risulterebbe chiusa alle 16:30. Il server lo sanifica e lo tiene
      // separato dall'ora di registrazione: non e' una scorciatoia per riscrivere
      // l'audit, e' il dato operativo che altrimenti si perde.
      const opHeaders = { ...headers };
      if (op.acting) opHeaders['X-Acting-Operator'] = encodeURIComponent(op.acting);
      if (op.timestamp) opHeaders['X-Client-Action-Ts'] = String(op.timestamp);
      // Stessa chiave a ogni tentativo: è ciò che rende il replay non duplicante.
      if (op.idempotencyKey) opHeaders['Idempotency-Key'] = op.idempotencyKey;
      const resp = await axios({
        method:  op.method,
        url:     remapValue(op.url, idMap),
        data,
        params:  remapValue(op.params, idMap),
        headers: opHeaders,
      });
      // Se è una creazione, registra la mappatura placeholder → id reale.
      if (op.clientId && resp?.data?.id) idMap[op.clientId] = resp.data.id;
      await db.pendingOps.delete(op.id);
      ok++;
    } catch (err) {
      const status = err?.response?.status;

      if (status === 401 || status === 429) {
        // TRANSITORI, non permanenti: 401 = token scaduto mentre si era offline
        // (dopo il re-login la coda riparte intatta), 429 = rate limit momentaneo.
        // Cancellare qui distruggerebbe il lavoro accodato offline. Interrompi il
        // ciclo: con lo stesso token/finestra fallirebbero anche le op successive.
        await db.pendingOps.update(op.id, {
          retries:   (op.retries || 0) + 1,
          lastError: err?.response?.data?.detail ?? err.message,
        });
        fail++;
        break;
      }

      const detail = err?.response?.data?.detail ?? err.message;

      if (status === 409) {
        // Conflitto: risorsa modificata mentre eravamo offline. Vince lo stato del
        // server, ma la versione dell'operatore non si butta: finisce fra le
        // rifiutate, cosi' puo' vedere cosa aveva scritto e decidere.
        // Contato solo come `conflicts`, non anche come `rejected`: il toast dei
        // conflitti dice già dove ritrovare la propria versione, sommarlo all'altro
        // contatore darebbe due avvisi per lo stesso evento.
        await moveToFailed(op, { status, error: detail, motivo: 'Conflitto: il dato è stato modificato da altri mentre eri offline' });
        conflicts++;
        if (op.clientId) rejected += await purgeDependents(op.clientId, 'conflitto');
      } else if (status >= 400 && status < 500) {
        // Errore client permanente (400, 403, 404, 422…): ritentare non aiuta, ma
        // nemmeno cancellare. Nota: un 403 qui e' plausibile sul PCA condiviso, dove
        // la coda viene sincronizzata col token di chi e' loggato ORA e non di chi ha
        // accodato — proprio il caso in cui buttare il dato sarebbe peggio.
        await moveToFailed(op, { status, error: detail, motivo: `Rifiutata dal server (HTTP ${status})` });
        rejected++;
        if (op.clientId) rejected += await purgeDependents(op.clientId, `HTTP ${status}`);
      } else {
        // Errore di rete o 5xx: transitorio — mantieni per il prossimo sync, ma non
        // all'infinito (vedi MAX_RETRIES).
        const tentativi = (op.retries || 0) + 1;
        if (tentativi >= MAX_RETRIES) {
          await moveToFailed(op, { status: status ?? null, error: detail, motivo: `Non riuscita dopo ${tentativi} tentativi` });
          rejected++;
          if (op.clientId) rejected += await purgeDependents(op.clientId, 'tentativi esauriti');
        } else {
          await db.pendingOps.update(op.id, { retries: tentativi, lastError: detail });
          fail++;
        }
      }
    }
  }

  return { ok, fail, conflicts, rejected, total: ops.length };
}
